import React from "react";
import { createRoot } from "react-dom/client";
import CommonTooltip from "./CommonTooltip";

// Single shared tooltip style/positioning used across the app - extracted
// from CommonButton.js so any element (not just CommonButton) can use the
// exact same look and the same collision-free positioning (fixed + portaled
// to document.body, so it's never clipped by a parent's overflow/z-index).

const POSITION_TRANSFORM_CLASSES = {
  top: "-translate-x-1/2 -translate-y-full",
  bottom: "-translate-x-1/2",
  left: "-translate-x-full -translate-y-1/2",
  right: "-translate-y-1/2",
};

// Arrow colours match CommonTooltip's blue bubble; only the two outer borders are drawn so the
// arrow merges with the bubble.
const ARROW_BASE =
  "before:content-[''] before:absolute before:w-2.5 before:h-2.5 before:bg-[#f0f6fe] before:border before:border-[#236DB4] before:rotate-45";
const POSITION_ARROW_CLASSES = {
  top: `${ARROW_BASE} before:left-1/2 before:bottom-[-5px] before:-translate-x-1/2 before:border-t-0 before:border-l-0`,
  bottom: `${ARROW_BASE} before:left-1/2 before:top-[-5px] before:-translate-x-1/2 before:border-b-0 before:border-r-0`,
  left: `${ARROW_BASE} before:right-[-5px] before:top-1/2 before:-translate-y-1/2 before:border-b-0 before:border-l-0`,
  right: `${ARROW_BASE} before:left-[-5px] before:top-1/2 before:-translate-y-1/2 before:border-t-0 before:border-r-0`,
};

const GAP = 12;

export const computeTooltipStyle = (rect, position) => {
  const style = { position: "fixed" };
  if (position === "top") {
    style.left = rect.left + rect.width / 2;
    style.top = rect.top - GAP;
  } else if (position === "bottom") {
    style.left = rect.left + rect.width / 2;
    style.top = rect.bottom + GAP;
  } else if (position === "left") {
    style.left = rect.left - GAP;
    style.top = rect.top + rect.height / 2;
  } else {
    style.left = rect.right + GAP;
    style.top = rect.top + rect.height / 2;
  }
  return style;
};

export const adjustTooltipPositionForRtl = (position, rtl) => {
  if (!rtl) return position;
  if (position === "left") return "right";
  if (position === "right") return "left";
  return position;
};

export const TooltipBubble = ({ text, position, rtl, style }) => (
  <div
    dir={rtl ? "rtl" : "ltr"}
    style={style}
    // pointer-events-none is load-bearing, not cosmetic: these controls are
    // packed tightly (e.g. map/satellite/zoom stacked ~40px apart), so a
    // tooltip bubble can visually overlap the next button. Without this, the
    // mouse would hover the bubble instead of passing through to the real
    // button underneath, so that button's mouseenter/mouseleave never fire
    // correctly and tooltips pile up stuck on screen.
    className={`pointer-events-none z-[9999999] px-3 py-1 text-xs font-semibold leading-snug text-[#236DB4] bg-[#f0f6fe]/95 border border-[#236DB4] rounded-md shadow-xl shadow-blue-900/5 backdrop-blur-md w-max max-w-[min(15rem,calc(100vw-1.5rem))] whitespace-normal break-words text-start [text-wrap:balance] transition-opacity duration-150 ${POSITION_TRANSFORM_CLASSES[position]} ${POSITION_ARROW_CLASSES[position]}`}
  >
    {text}
  </div>
);

/**
 * Wraps a single child (usually a button) and shows the shared tooltip on
 * hover/focus, positioned from the child's real rendered rect.
 */
// Delegates to the shared CommonTooltip, so long messages wrap inside a max width, the bubble
// stays inside the window and every screen looks the same. `disabled` just drops the text.
const Tooltip = ({ text, position = "top", children, className = "", disabled = false }) => (
  <CommonTooltip
    text={disabled ? "" : text}
    position={position}
    disableDefaultDescription
    className={`relative inline-block ${className}`}
    tooltipClassName="w-max max-w-[min(12rem,calc(100vw-1.5rem))] [text-wrap:balance]"
    fitContent
    clampToViewport
    hoverOnly
  >
    {children}
  </CommonTooltip>
);

export default Tooltip;

/**
 * Imperative version for non-React DOM nodes (e.g. Leaflet controls built
 * via L.DomUtil.create) - same visual bubble, mounted/unmounted on native
 * mouseenter/mouseleave/focus/blur instead of React hover state.
 * Returns a cleanup function.
 */
export const mountImperativeTooltip = (anchorEl, text, { position = "top", rtl = false } = {}) => {
  if (!anchorEl || !text) return () => {};

  let portalRoot = null;
  let container = null;

  const hide = () => {
    if (portalRoot) {
      portalRoot.unmount();
      portalRoot = null;
    }
    if (container) {
      container.remove();
      container = null;
    }
  };

  const show = () => {
    // Idempotent: if a bubble is somehow already mounted (e.g. mouseenter
    // fired again without a mouseleave in between), clear it first instead
    // of overwriting the refs and leaking the old DOM node forever.
    hide();
    const rect = anchorEl.getBoundingClientRect();
    container = document.createElement("div");
    document.body.appendChild(container);
    portalRoot = createRoot(container);
    portalRoot.render(
      <TooltipBubble text={text} position={position} rtl={rtl} style={computeTooltipStyle(rect, position)} />
    );
  };

  anchorEl.addEventListener("mouseenter", show);
  anchorEl.addEventListener("mouseleave", hide);
  anchorEl.addEventListener("focus", show);
  anchorEl.addEventListener("blur", hide);

  return () => {
    hide();
    anchorEl.removeEventListener("mouseenter", show);
    anchorEl.removeEventListener("mouseleave", hide);
    anchorEl.removeEventListener("focus", show);
    anchorEl.removeEventListener("blur", hide);
  };
};
